import { Inject, Injectable, OnDestroy } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { Socket, io } from 'socket.io-client';
import {
    BehaviorSubject,
    Subject,
    firstValueFrom,
    timeout,
    take,
    of,
} from 'rxjs';
import {
    BiddingService,
    GetAllWatchersResponse,
    Watcher,
} from '@viewbid/ts-angular-client';
import { catchError, distinctUntilChanged, map } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { TokenStorageService } from 'viewbid-shared-lib';
import { selectUser } from '../../state/app-selectors';
import { AppState } from '../../state/app-state';

export type EventListeners = { resourceId: string; listen: boolean };

export interface NotificationData {
    title: string;
    description: string;
    timestamp: string;
    type: string;
    moreDetails: string;
    isViewed: boolean;
}
type UserLike = { accountMappingsId?: string | null };
type JsonLike = unknown;

export type ConnectionStatus = 'connected' | 'reconnecting' | 'disconnected';

const SERVER_TIME_EVENT: string = 'time';

function isEpochMs(value: unknown): value is number {
    return typeof value === 'number' && Number.isFinite(value);
}

function serverTimeOf(reply: unknown): number | null {
    return typeof reply === 'object' &&
        reply !== null &&
        'serverTime' in reply &&
        isEpochMs(reply.serverTime)
        ? reply.serverTime
        : null;
}

function sentAtOf(stamp: unknown): number | null {
    return typeof stamp === 'object' &&
        stamp !== null &&
        'sentAt' in stamp &&
        isEpochMs(stamp.sentAt)
        ? stamp.sentAt
        : null;
}

const HANDSHAKE_RETRY_BASE_MS: number = 1000;
const HANDSHAKE_RETRY_MAX_MS: number = 60_000;
const RENEWED_TOKEN_CHECK_MS: number = 5000;
const TOKEN_EXPIRED_REJECTION: string = 'TOKEN_EXPIRED';

export function handshakeRetryDelayMs(
    rejections: number,
    random: () => number = Math.random,
): number {
    const ceiling: number = Math.min(
        HANDSHAKE_RETRY_MAX_MS,
        HANDSHAKE_RETRY_BASE_MS * 2 ** (rejections - 1),
    );
    return Math.round(ceiling * (0.5 + random() * 0.5));
}

@Injectable({ providedIn: 'root' })
export class SocketService implements OnDestroy {
    // Generate unique ID for this client
    private snowflake: string | null = null;

    // Store reference to socket (Socket defaults to DefaultEventsMap maps)
    private socket!: Socket;

    /** Socket factory seam: webpack emits ESM exports read-only, so specs
     *  can't spy on io() at the module level — they swap this field instead. */
    private ioFactory: typeof io = io;
    private socketPath = '/sockets/socket.io/'; // align with backend

    // Intentional close ('io client disconnect'): no auto-reconnect, so the
    // connectivity/foreground handlers must not try to revive it.
    private intentionalDisconnect: boolean = false;

    private handshakeRejections: number = 0;
    private handshakeRetryTimer: ReturnType<typeof setTimeout> | null = null;
    private handshakeToken: string | null = null;
    private expiredToken: string | null = null;
    private renewedTokenCheck: ReturnType<typeof setInterval> | null = null;

    // Guards resyncOnReachable so a burst of connectivity events kicks at most
    // one wake while a reconnect is pending; cleared when the attempt settles.
    private resyncPending: boolean = false;

    // Bound listeners, retained so they can be removed on destroy.
    private readonly onOnline: () => void = () => this.resyncOnReachable();
    private readonly onVisibilityChange: () => void = () => {
        if (this.doc?.visibilityState === 'visible') this.resyncOnReachable();
    };

    /** The window owning our injected document — the browser-globals seam.
     *  window/document are unpatchable in modern Chrome, so tests fake the
     *  DOCUMENT constructor param instead of monkey-patching globals. */
    private get win(): (Window & typeof globalThis) | null {
        return this.doc?.defaultView ?? null;
    }

    // identity currently connected to the socket (ANON:xxx or userId) - Guest ANON - loged userId
    private connectedUid: string | null = null;
    private socketUid: string | null = null;

    // cache of watchers to re-subscribe
    private currentWatchers: Watcher[] = [];

    /** Every resource room this session wants (listen:true minus listen:false).
     *  Server-side membership dies with the socket session — a non-recovered
     *  reconnect starts with zero rooms — so the connect handler replays this
     *  registry on every connect. Pages therefore never need their own
     *  re-join-on-reconnect logic; their reconnected$ REST refetch only has to
     *  repaint data missed while down. */
    private readonly joinedResources: Set<string> = new Set<string>();

    private resourceSubject = new Subject<string>();
    private readonly incrementPublications: Set<string> = new Set<string>();
    public resource$ = this.resourceSubject.asObservable();

    private isRepeatedIncrementPublication(value: unknown): boolean {
        if (!value || typeof value !== 'object') return false;
        const event: Record<string, unknown> = value as Record<string, unknown>;
        const id: unknown = event['publicationId'];
        if (
            event['action'] !== 'BID_INCREMENT_UPDATE' ||
            typeof id !== 'string'
        )
            return false;
        if (this.incrementPublications.has(id)) return true;
        this.incrementPublications.add(id);
        if (this.incrementPublications.size > 1000) {
            const oldest: string | undefined = this.incrementPublications
                .values()
                .next().value;
            if (oldest) this.incrementPublications.delete(oldest);
        }
        return false;
    }

    // Fires on a reconnect the server did not recover: consumers REST-resync.
    private reconnectedSubject = new Subject<void>();
    public reconnected$ = this.reconnectedSubject.asObservable();

    // Connection status for the reconnecting indicator; lazy socket starts closed.
    private connectionStatusSubject = new BehaviorSubject<ConnectionStatus>(
        'disconnected',
    );
    public connectionStatus$ = this.connectionStatusSubject.asObservable();

    // Notification subject and observable
    private notificationSubject = new Subject<NotificationData>();
    public notification$ = this.notificationSubject.asObservable();

    private connectedSubject = new Subject<void>();
    public connected$ = this.connectedSubject.asObservable();

    private sentAtSubject = new Subject<number>();
    public sentAt$ = this.sentAtSubject.asObservable();

    constructor(
        private biddingService: BiddingService,
        private store: Store<AppState>,
        private tokenStorageService: TokenStorageService,
        @Inject(DOCUMENT) private readonly doc: Document,
    ) {
        this.snowflake =
            localStorage.getItem('SnowFlakeId') ?? this.fetchSnowFlakeId();
        // starts “lazy”: only connects when it needs to emit OR when userId already exists
        this.bootstrapIfIdentified().catch(() => void 0);
        this.attachConnectivityResync();
    }

    // Confirm reachability via socket state; navigator.onLine is unreliable.
    private attachConnectivityResync(): void {
        this.win?.addEventListener?.('online', this.onOnline);
        this.doc?.addEventListener?.(
            'visibilitychange',
            this.onVisibilityChange,
        );
    }

    // On connectivity/foreground return, wake a non-intentionally-closed socket.
    // The connect handler is the sole emitter of the resync signal; a pending
    // guard collapses a burst of connectivity events to a single wake.
    private resyncOnReachable(): void {
        if (this.intentionalDisconnect) return;
        if (this.socket?.connected) return;
        if (this.resyncPending) return;
        if (this.socket && !this.socket.active) {
            this.retryRejectedHandshakeNow();
            return;
        }
        // Wake the same session; the connect handler signals the REST backstop
        // and settleConnect clears the pending flag.
        this.resyncPending = true;
        // Force immediate attempt; backgrounded tabs throttle socket.io's internal reconnect backoff timers.
        this.socket?.io?.open();
        this.ensureSocketWithLatestIdentity().catch(() => void 0);
    }

    ngOnDestroy(): void {
        this.clearHandshakeRetry();
        this.stopRenewedTokenCheck();
        this.win?.removeEventListener?.('online', this.onOnline);
        this.doc?.removeEventListener?.(
            'visibilitychange',
            this.onVisibilityChange,
        );
    }

    private fetchSnowFlakeId(): string {
        const id: string = crypto.randomUUID();
        localStorage.setItem('SnowFlakeId', id);
        return id;
    }

    /** permite rebootstrap manual (AppComponent) */
    public initialize(): void {
        this.bootstrapIfIdentified().catch(() => void 0);
    }

    /** Attempts to open a connection immediately if a userId exists; otherwise stays anonymous. */
    private async bootstrapIfIdentified(): Promise<void> {
        const id: string | null = await this.getLatestUserId();
        await this.ensureSocketWithLatestIdentity(id);
        if (id) {
            // if already logged in, pre-load watchers and subscribe to resources
            this.loadAndSubscribeWatchers().catch(() => void 0);
        }
    }

    /** Always call this before using the socket. */
    private async ensureSocketWithLatestIdentity(
        forceId?: string | null,
    ): Promise<void> {
        const latestId: string | null =
            forceId ?? (await this.getLatestUserId());
        const targetUid: string =
            latestId && latestId.trim() ? latestId : `ANON:${this.snowflake}`;

        if (this.socket?.connected && this.connectedUid === targetUid) {
            this.reauthenticateWithRenewedToken();
            return;
        }

        // if identity changed OR socket does not exist/is not connected, reconnect
        await this.reconnect(targetUid);
    }

    private async getLatestUserId(): Promise<string | null> {
        try {
            const id: string | null = await firstValueFrom(
                this.store.select(selectUser).pipe(
                    map(
                        (user: UserLike | null) =>
                            user?.accountMappingsId ?? null,
                    ),
                    distinctUntilChanged(),
                    take(1),
                    timeout(1000),
                    catchError(() => of(null)),
                ),
            );
            return id && id.trim() ? id : null;
        } catch {
            return null;
        }
    }

    // Settles the in-flight connect promise; handlers register once per socket.
    private pendingConnectResolve: (() => void) | null = null;

    private settleConnect(): void {
        // A settled connect attempt (success or failure) re-opens the resync
        // guard so the next connectivity event can wake the socket.
        this.resyncPending = false;
        const resolve: (() => void) | null = this.pendingConnectResolve;
        this.pendingConnectResolve = null;
        resolve?.();
    }

    // Register lifecycle handlers once per socket so reconnects never stack them.
    private attachLifecycleHandlers(uid: string): void {
        // First connect of this socket is initial, not a reconnect: no resync.
        let hasConnectedOnce: boolean = false;

        this.socket.on('connect', () => {
            const isReconnect: boolean = hasConnectedOnce;
            hasConnectedOnce = true;
            this.handshakeRejections = 0;
            if (this.isGuestForExpiredToken()) {
                this.startRenewedTokenCheck();
            } else {
                this.stopRenewedTokenCheck();
            }

            this.socket.emit('hello', {
                ssoId: uid,
                snowflake: this.snowflake,
                demo: 'window.NAME',
            });
            this.connectedUid = uid;

            // Rebuild server-side room membership from the session registry.
            // Rooms die with the socket session (a non-recovered reconnect
            // starts with none) and joins are idempotent, so replay on every
            // connect — a recovered session just re-asserts what it has. This
            // also delivers any joins requested while disconnected.
            this.joinedResources.add('BID_INCREMENT_UPDATE');
            this.replayJoinedResources();

            // Recovered sessions replay missed events; otherwise REST-resync.
            if (isReconnect && !this.socket.recovered) {
                this.reconnectedSubject.next();
            }
            this.connectionStatusSubject.next('connected');
            this.settleConnect();
            this.connectedSubject.next();
        });

        this.socket.on('connect_error', (error: Error) => {
            this.settleConnect();
            if (!this.socket.active && !this.intentionalDisconnect) {
                if (error?.message === TOKEN_EXPIRED_REJECTION) {
                    this.expiredToken = this.handshakeToken;
                }
                this.handshakeRejections++;
                this.scheduleHandshakeRetry();
                this.startRenewedTokenCheck();
            }
        });

        this.socket.on('disconnect', (reason: Socket.DisconnectReason) => {
            this.connectionStatusSubject.next('disconnected');
            this.settleConnect();
            // socket.io disables auto-reconnect on a server kick; revive the
            // same socket unless the close was deliberate.
            if (
                reason === 'io server disconnect' &&
                !this.intentionalDisconnect
            ) {
                this.socket.connect();
            }
        });

        // Manager-level retry events drive the 'reconnecting' indicator.
        const manager: Socket['io'] | undefined = this.socket.io;
        manager?.on('reconnect_attempt', () => {
            this.connectionStatusSubject.next('reconnecting');
        });
        manager?.on('reconnect', () => {
            this.connectionStatusSubject.next('connected');
        });
    }

    private connectionPromise: Promise<void> | null = null;

    private async reconnect(uid: string): Promise<void> {
        if (this.connectionPromise) {
            return this.connectionPromise;
        }

        // Reuse the existing socket unless identity changed: resuming the same
        // session lets the server replay missed events.
        const identityChanged: boolean =
            !!this.socket && this.socketUid !== uid;
        if (this.socket && !identityChanged) {
            if (this.socket.connected || !this.socket.active) {
                return;
            }
            // Await the existing handlers; do not re-register them.
            this.connectionPromise = (async (): Promise<void> => {
                try {
                    await new Promise<void>((resolve: () => void) => {
                        this.pendingConnectResolve = resolve;
                    });
                } finally {
                    this.connectionPromise = null;
                }
            })();
            return this.connectionPromise;
        }

        this.connectionPromise = (async (): Promise<void> => {
            try {
                // Tear down only when an existing socket belongs to a different
                // identity; never on a plain reconnect of the same session.
                if (identityChanged) {
                    try {
                        // removeAllListeners() only clears Socket-level handlers;
                        // drop the Manager reconnect listeners too so a user
                        // switch leaves none behind.
                        this.socket.io?.off('reconnect_attempt');
                        this.socket.io?.off('reconnect');
                        this.socket.removeAllListeners();
                        this.socket.disconnect();
                        this.clearHandshakeRetry();
                        this.stopRenewedTokenCheck();
                        this.handshakeRejections = 0;
                        this.expiredToken = null;
                    } catch {
                        /* empty */
                    }
                }

                // create new connection
                // Exponential backoff, retry indefinitely; allow polling so
                // live updates survive networks that block plain websockets.
                this.socket = this.ioFactory(this.win?.location?.origin ?? '', {
                    transports: ['websocket', 'polling'],
                    reconnection: true,
                    reconnectionDelay: 1000,
                    reconnectionDelayMax: 5000,
                    randomizationFactor: 0.5,
                    reconnectionAttempts: Infinity,
                    path: this.socketPath,
                    // The gateway resolves identity from the verified JWT at
                    // the handshake (the hello ssoId payload is ignored
                    // server-side). Without it the socket joins an ANON user
                    // room and every user-targeted event — the OUT_BID /
                    // NEW_HIGH_BID outbid toast, USER_MAX_BID_UPDATE — is
                    // silently lost.
                    auth: (cb: (data: object) => void): void =>
                        cb(this.handshakeAuth()),
                });
                this.socketUid = uid;
                // Fresh connection clears the intentional-close flag.
                this.intentionalDisconnect = false;

                await new Promise<void>((resolve: () => void) => {
                    this.pendingConnectResolve = resolve;
                    this.attachLifecycleHandlers(uid);
                });

                // listeners
                this.socket
                    .off('resources')
                    .on('resources', (data: JsonLike, stamp?: unknown) => {
                        this.publishSentAt(stamp);
                        try {
                            const s: string =
                                typeof data === 'string'
                                    ? data
                                    : JSON.stringify(data);
                            const event: unknown = JSON.parse(s);
                            if (this.isRepeatedIncrementPublication(event))
                                return;
                            this.resourceSubject.next(s);
                        } catch {
                            /* silent */
                        }
                    });

                this.socket
                    .off('notification')
                    .on('notification', (data: JsonLike, stamp?: unknown) => {
                        this.publishSentAt(stamp);
                        try {
                            const obj: NotificationData =
                                typeof data === 'string'
                                    ? JSON.parse(data)
                                    : (data as NotificationData);
                            this.notificationSubject.next(obj);
                        } catch {
                            /* silent */
                        }
                    });
            } finally {
                this.connectionPromise = null;
            }
        })();

        return this.connectionPromise;
    }

    private publishSentAt(stamp: unknown): void {
        const sentAt: number | null = sentAtOf(stamp);
        if (sentAt !== null) this.sentAtSubject.next(sentAt);
    }

    private handshakeAuth(): { token?: string } {
        const token: string | null = this.tokenStorageService.getToken();
        this.handshakeToken =
            token && token !== this.expiredToken ? token : null;
        return this.handshakeToken ? { token: this.handshakeToken } : {};
    }

    private isGuestForExpiredToken(): boolean {
        return !this.handshakeToken && !!this.expiredToken;
    }

    private scheduleHandshakeRetry(): void {
        const socket: Socket = this.socket;
        this.clearHandshakeRetry();
        this.handshakeRetryTimer = setTimeout(() => {
            this.handshakeRetryTimer = null;
            if (
                socket === this.socket &&
                !socket.active &&
                !this.intentionalDisconnect
            ) {
                socket.connect();
            }
        }, handshakeRetryDelayMs(this.handshakeRejections));
    }

    private retryRejectedHandshakeNow(): void {
        this.clearHandshakeRetry();
        this.socket.connect();
    }

    private clearHandshakeRetry(): void {
        if (this.handshakeRetryTimer) {
            clearTimeout(this.handshakeRetryTimer);
            this.handshakeRetryTimer = null;
        }
    }

    private startRenewedTokenCheck(): void {
        this.stopRenewedTokenCheck();
        this.renewedTokenCheck = setInterval(
            () => this.reauthenticateWithRenewedToken(),
            RENEWED_TOKEN_CHECK_MS,
        );
    }

    private stopRenewedTokenCheck(): void {
        if (this.renewedTokenCheck) {
            clearInterval(this.renewedTokenCheck);
            this.renewedTokenCheck = null;
        }
    }

    private reauthenticateWithRenewedToken(): void {
        const token: string | null = this.tokenStorageService.getToken();
        if (
            !token ||
            token === this.expiredToken ||
            token === this.handshakeToken
        )
            return;
        if (this.socket?.connected && this.isGuestForExpiredToken()) {
            this.stopRenewedTokenCheck();
            this.socket.disconnect();
            this.socket.connect();
        } else if (
            this.socket &&
            !this.socket.active &&
            !this.intentionalDisconnect
        ) {
            this.stopRenewedTokenCheck();
            this.retryRejectedHandshakeNow();
        }
    }

    /** Fetch user watchers (if logged in) and re-subscribe. */
    private async loadAndSubscribeWatchers(): Promise<void> {
        const resp: GetAllWatchersResponse = await firstValueFrom(
            this.biddingService.getMyWatchers(1, -1),
        );
        this.currentWatchers = resp.data ?? [];
        this.reSubscribeToResources(this.currentWatchers);
    }

    private reSubscribeToResources(response: Array<Watcher>): void {
        if (!response) return;
        const resources: EventListeners[] = response
            .filter((item: Watcher) => !!item.auctionLotId)
            .map((item: Watcher) => ({
                resourceId: item.auctionLotId,
                listen: true,
            }));
        this.subscribeToResources(resources);
    }

    /** Re-emits a join for every room in the session registry. One message
     *  per resource, matching subscribeToResources (backend compatibility). */
    private replayJoinedResources(): void {
        for (const resourceId of this.joinedResources) {
            this.socket.emit('resources', [{ resourceId, listen: true }]);
        }
    }

    // ====== Public API ======

    /** Always use this method: it ensures identity before emitting. */
    public async subscribeToResources(
        message: EventListeners[],
    ): Promise<void> {
        // Record intent before any connectivity checks: the registry is the
        // durable source of truth, and the connect replay delivers whatever
        // can't be emitted right now.
        message.forEach((msg: EventListeners) => {
            if (msg.listen) {
                this.joinedResources.add(msg.resourceId);
            } else {
                this.joinedResources.delete(msg.resourceId);
            }
        });

        await this.ensureSocketWithLatestIdentity(); // <- checks and upgrades from ANON -> user, if applicable

        if (!this.socket?.connected) {
            // Joins are covered by the connect replay; a leave needs no send
            // because the next socket session starts with no rooms anyway.
            return;
        }

        // send one by one (backend compatibility)
        message.forEach((msg: EventListeners) => {
            this.socket.emit('resources', [
                { resourceId: msg.resourceId, listen: msg.listen },
            ]);
        });
    }

    public get isConnected(): boolean {
        return !!this.socket?.connected;
    }

    public async requestServerTime(timeoutMs: number): Promise<number | null> {
        if (!this.socket?.connected) return null;
        try {
            return serverTimeOf(
                await this.socket
                    .timeout(timeoutMs)
                    .emitWithAck(SERVER_TIME_EVENT),
            );
        } catch {
            return null;
        }
    }

    public disconnect(): void {
        // Deliberate close: handlers must not try to revive it.
        this.intentionalDisconnect = true;
        this.clearHandshakeRetry();
        this.stopRenewedTokenCheck();
        this.expiredToken = null;
        try {
            this.socket?.disconnect();
        } catch {
            /* empty */
        }
        this.connectedUid = null;
        this.socketUid = null;
    }
}
